import { FormContextType, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileItem } from '@/components/core/Form/widgets/FileWidget/FileItem';
import PopupNotification from '@/domain/core/PopupNotification';
import { AjvErrorObject } from '@/features/shared/types/ajvErrorObject';
import { bytesToMegabytes } from '@/features/shared/utils/bytesToMegabytes';
import { getExtensionsByMimeTypes } from '@/features/shared/utils/getExtensionsByMimeTypes';
import { Dropzone } from '@/ui/Dropzone';
import { backendApi } from '@/utils/api';

export const removeFile = async (id: string) => {
	try {
		await backendApi.delete<string>(`v2/files/${id}`);
		return true;
	} catch (e) {
		return false;
	}
};

export const uploadFile = async (selectionId: string, fieldName: string, file: File, forPassport: boolean = false) => {
	const formData = new FormData();
	formData.append('file', file);

	const { data } = await backendApi.post<string>(`v2/files/selections/${selectionId}/fields/${fieldName}`, formData, {
		params: {
			forPassport
		}
	});

	return data;
};

export const uploadFiles = async (selectionId: string, fieldName: string, files: File[], forPassport: boolean = false) => {
	const formData = new FormData();

	files.forEach((file) => {
		formData.append('file', file);
	});
	const { data } = await backendApi.post<string[]>(
		`v2/files/selections/${selectionId}/fields/${fieldName}/multi`,
		formData,
		{
			params: {
				forPassport
			}
		}
	);

	return data;
};

export function getValueAsArray(value: unknown) {
	if (Array.isArray(value)) {
		// Фильтруем все значения что бы исключить undefined, так как если задать minItems больше нулю, то в value будет состоять из [undefined, ...n] и мешать валидации если установлено maxItems
		return (value as unknown[]).filter((item) => typeof item === 'string') as string[];
	}

	if (typeof value === 'string' && value) {
		return [value];
	}
	return [];
}

const FileWidget = <T, S extends StrictRJSFSchema, F extends FormContextType>(props: WidgetProps<T, S, F>) => {
	const { id, options, schema, formContext, name, readonly, onChange, uiSchema } = props;

	const forPassport = id.toString().startsWith('passport');
	const value = props.value as unknown;
	const [error, setError] = useState<AjvErrorObject | string>();
	const [loading, setLoading] = useState(false);

	const { t } = useTranslation();
	const multiple = !!options.multiple;
	const accept = typeof options.accept === 'string' ? options.accept : undefined;

	const valueAsArray = useMemo(() => getValueAsArray(value), [value]);

	const onDrop = async (files: File[]) => {
		setError(undefined);

		if (
			!('selectionId' in formContext) ||
			(typeof formContext.selectionId !== 'string' && typeof formContext.selectionId !== 'number')
		) {
			throw new Error('Form context should have selectionId field');
		}

		setLoading(true);

		try {
			if (multiple) {
				const ids = await uploadFiles(formContext.selectionId.toString(), name, files, forPassport);
				onChange([...valueAsArray, ...ids].filter(Boolean));
			} else {
				const id = await uploadFile(formContext.selectionId.toString(), name, files[0], forPassport);

				if (value) {
					await removeFile(value as string);
				}

				onChange(id);
			}
		} catch (unknownError: unknown) {
			const axiosError = unknownError as AxiosError<{ message: AjvErrorObject[] }>;

			if (Array.isArray(axiosError.response?.data?.message)) {
				setError(axiosError.response.data.message[0]);
			} else if (typeof axiosError.response?.data?.message === 'string') {
				setError(axiosError.response.data.message);
			} else {
				setError(t('errors.uploadFile'));
			}
		} finally {
			setLoading(false);
		}
	};

	const onRemove = async (id: string) => {
		if (await removeFile(id)) {
			if (Array.isArray(value)) {
				onChange(value.filter((el) => el !== id));
			} else {
				onChange(['']);
			}
		}
	};

	const maxFileSize = typeof uiSchema['ui:options']?.size === 'number' ? uiSchema['ui:options']?.size : undefined;
	const availableExtensions = accept ? getExtensionsByMimeTypes(accept.split(',')) : [];

	useEffect(() => {
		// NOTE: Skip minItems value when schema changed
		if (Array.isArray(value) && !value.filter(Boolean).length && !schema.minItems) {
			onChange(['']);
		}
	}, [value, schema]);

	useEffect(() => {
		if (error) {
			if (typeof error === 'string') {
				console.log(error);
			} else {
				if (error.instancePath) {
					if (error.instancePath.endsWith('/mimetype')) {
						PopupNotification.emitter('error', t('form.validation.error.file.mimeType'));
					}

					if (error.instancePath.endsWith('/size')) {
						PopupNotification.emitter('error', t('form.validation.error.file.size'));
					}
				}
				console.log(error);
			}
		}
	}, [error]);

	return (
		<div className={clsx('mb-2', { 'opacity-50 cursor-not-allowed': readonly })}>
			<div className="text-xs my-1 text-brand-gray-2 flex gap-1 flex-wrap">
				{maxFileSize && <span> {t('form.validation.file.size', { size: bytesToMegabytes(maxFileSize) })}</span>}
				{availableExtensions.length > 0 && (
					<span>{t('form.validation.file.mimeType', { extensions: availableExtensions.join(', ') })}</span>
				)}
			</div>
			<div className="relative">
				<Dropzone
					name={name}
					readonly={readonly}
					onDrop={onDrop}
					description={schema.description || options.description}
					accept={accept}
					multiple={multiple}
					maxSize={maxFileSize}
					className={clsx('mb-2', { 'opacity-50 cursor-not-allowed': readonly || loading })}
				/>
				{loading && (
					<div className="absolute flex inset-0 items-center justify-center">
						<Loader2 className="animate-spin" />
					</div>
				)}
			</div>
			<div className="flex flex-col gap-2">
				{valueAsArray.map((id) => (
					<FileItem id={id} key={id} onRemove={!readonly ? () => onRemove(id) : undefined} />
				))}
			</div>
		</div>
	);
};

export { FileWidget };
